import api from '../lib/api';
import type {
  AuthResponse,
  RegisterRequest,
  User,
  TwoFactorSetupResponse,
  TwoFactorStatusResponse,
  TwoFactorLoginResponse,
} from '../types/api';

class AuthService {
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/users/login', {
      username,
      password,
    });
    return response.data;
  }

  async loginWithTwoFactor(
    twoFactorToken: string,
    code: string
  ): Promise<TwoFactorLoginResponse> {
    const response = await api.post<TwoFactorLoginResponse>('/users/login/2fa', {
      twoFactorToken,
      code,
    });
    return response.data;
  }

  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    const response = await api.post<TwoFactorSetupResponse>('/users/2fa/setup');
    return response.data;
  }

  async verifyTwoFactorSetup(code: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post<{ success: boolean; message?: string }>('/users/2fa/verify', {
      code,
    });
    return response.data;
  }

  async disableTwoFactor(code: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post<{ success: boolean; message?: string }>('/users/2fa/disable', {
      code,
    });
    return response.data;
  }

  async getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
    const response = await api.get<TwoFactorStatusResponse>('/users/2fa/status');
    return response.data;
  }

  async register(data: RegisterRequest): Promise<any> {
    try {
      const response = await api.post<AuthResponse>('/users/register', data);
      return response.data;
    } catch (error: any) {
      // Return the error response data if available
      if (error.response && error.response.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/users/refresh-token', {
      refreshToken,
    });
    return response.data;
  }

  async getUserInfo(): Promise<User> {
    const response = await api.get<User>('/users/me');
    return response.data;
  }

  async updateUserInfo(data: Partial<User>): Promise<User> {
    const response = await api.put<User>('/users/me', data);
    return response.data;
  }

  async forgotPassword(email: string): Promise<void> {
    await api.post('/users/forgot-password', { email });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/users/reset-password', { token, password });
  }

  async getAffiliateInfo(affiliateCode: string): Promise<any> {
    const response = await api.get(`/users/affiliate/${affiliateCode}`);
    return response.data;
  }
}

export default new AuthService();
